import React, { useState, useEffect, useCallback } from "react";
import { FiAlertCircle, FiSearch, FiRefreshCw } from "react-icons/fi";
import { fetchPointsLedger } from "../../../utils/eventApi";

/**
 * Every discrete award across one event, newest first.
 *
 * The event-wide question rather than the per-user one: "who got an adjustment
 * this week", "did the Creator Hub hook double-fire on the 12th". A per-user
 * drawer cannot answer either, because the pattern only shows across users.
 *
 * **Mining is not here, and that is not a bug.** It is credited on every
 * coin-update poll and is recorded per day per user instead — billions of ledger
 * rows otherwise. The note at the foot of the table says so.
 *
 * Paged by cursor rather than page number: at a million users this table runs to
 * tens of millions of rows, and `OFFSET 500000` re-reads everything before it.
 * That is also why there is a Load more button and no page selector — with a
 * keyset there is no such thing as jumping to page 900.
 */

const SOURCE_LABEL = {
  daily_check_in: "Check-in",
  creator_hub: "Creator Hub",
  admin_adjustment: "Admin adjustment",
  prize_px: "Prize",
  task: "Task",
  tap_game: "Tap Game",
  boom_cards: "Boom Cards",
  mini_game: "Game",
  referral: "Referral join",
  exchange: "Exchange",
  exchange_refund: "Exchange refund",
};

const SOURCE_STYLE = {
  daily_check_in: "bg-indigo-50 text-indigo-700",
  creator_hub: "bg-violet-50 text-violet-700",
  admin_adjustment: "bg-rose-50 text-rose-700",
  prize_px: "bg-amber-50 text-amber-700",
  task: "bg-blue-50 text-blue-700",
  tap_game: "bg-emerald-50 text-emerald-700",
  boom_cards: "bg-emerald-50 text-emerald-700",
  mini_game: "bg-emerald-50 text-emerald-700",
  referral: "bg-sky-50 text-sky-700",
  exchange: "bg-gray-100 text-gray-700",
  exchange_refund: "bg-gray-100 text-gray-700",
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const fmtTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const PointsLedgerTab = ({ eventId, onOpenHistory }) => {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPointsLedger(eventId, { source, search: applied });
      setRows(res.data?.data || []);
      setCursor(res.data?.next_cursor || null);
    } catch (err) {
      setError(err.message || "Could not load the points ledger");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, source, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await fetchPointsLedger(eventId, { source, search: applied, cursor });
      setRows((prev) => [...prev, ...(res.data?.data || [])]);
      setCursor(res.data?.next_cursor || null);
    } catch (err) {
      setError(err.message || "Could not load more rows");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(search.trim());
          }}
          className="relative flex-1 min-w-[180px]"
        >
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search username or email"
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-300"
          />
        </form>

        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 bg-white"
        >
          <option value="">All sources</option>
          {Object.entries(SOURCE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>

        <button
          onClick={load}
          disabled={loading}
          title="Reload"
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
        >
          <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="px-4 py-2.5 font-medium">When</th>
              <th className="px-4 py-2.5 font-medium">User</th>
              <th className="px-4 py-2.5 font-medium">Source</th>
              <th className="px-4 py-2.5 font-medium text-right">Points</th>
              <th className="px-4 py-2.5 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            )}

            {!loading && error && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center">
                  <FiAlertCircle className="mx-auto mb-2 text-rose-400" size={20} />
                  <p className="text-gray-600">{error}</p>
                </td>
              </tr>
            )}

            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                  {applied || source
                    ? "Nothing matches those filters."
                    : "No individual awards recorded for this event yet."}
                </td>
              </tr>
            )}

            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50/70 transition border-b border-gray-50">
                <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{fmtTime(r.at)}</td>
                <td className="px-4 py-2.5">
                  {r.user ? (
                    <button
                      onClick={() => onOpenHistory && onOpenHistory(r.user)}
                      className="text-gray-700 hover:text-indigo-600 hover:underline font-medium"
                      title="Open this user's full history"
                    >
                      {r.user.username}
                    </button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      SOURCE_STYLE[r.source] || "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {SOURCE_LABEL[r.source] || r.source}
                  </span>
                </td>
                <td
                  className={`px-4 py-2.5 text-right tabular-nums font-semibold ${
                    Number(r.points) < 0 ? "text-rose-600" : "text-gray-800"
                  }`}
                >
                  {Number(r.points) > 0 ? "+" : ""}{fmt(r.points)}
                </td>
                <td className="px-4 py-2.5 text-gray-500">
                  {r.note && <span>{r.note}</span>}
                  {r.ref_type && (
                    <span className="text-gray-400">
                      {r.note ? " · " : ""}{r.ref_label ? r.ref_label : `${r.ref_type} #${r.ref_id}`}
                    </span>
                  )}
                  {r.admin && (
                    <span className="text-gray-400">
                      {r.note || r.ref_type ? " · " : ""}by {r.admin.name || `admin #${r.admin.id}`}
                    </span>
                  )}
                  {!r.note && !r.ref_type && !r.admin && <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Keyset paging — there is no page number to jump to */}
      {cursor && (
        <div className="px-4 py-3 border-t border-gray-100">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="w-full py-2 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}

      <p className="px-4 py-3 border-t border-gray-100 text-[11px] text-gray-400 leading-relaxed">
        Individual awards only — check-ins, Creator Hub milestones, admin adjustments
        and prizes. Mining, streak and referral points are recorded per day rather
        than per award; open a user's History to see those.
      </p>
    </div>
  );
};

export default PointsLedgerTab;
